import { logger } from '@ds/swymkit/utils'

type PromiseResolve<FetchReturns> = (value: FetchReturns | PromiseLike<FetchReturns>) => void
type PromiseReject = (reason?: unknown) => void
type Item<FetchOptions, ResolveReturns> = [FetchOptions, PromiseResolve<ResolveReturns>, PromiseReject]

/** sets the maximum number of items in the bucket before triggering a batch request */
const BUCKET_LIMIT = 10
/** sets the throttle time (in milliseconds) for batching requests */
const THROTTLE_MS = 200

type TFetch<FetchOptions, FetchReturns>
  = (option: FetchOptions) => Promise<FetchReturns>
type TMerge<MutualizerOptions, FetchOptions>
  = (bucket: MutualizerOptions[]) => FetchOptions
type TResolve<MutualizerOptions, FetchReturns, ResolveReturns>
  = ([response, item]: [FetchReturns, Item<MutualizerOptions, ResolveReturns>]) => void

/**
 * mutualizerDecorator function is a decorator that wraps an asynchronous operation,
 * batching requests and optimizing their execution based on specified configurations.
 *
 * This can be useful for scenarios where you want to minimize the number of network requests
 * or improve efficiency by grouping multiple asynchronous operations.
 *
 * Generic type:
 * - FetchReturns -> typeof fetch function
 * - MutualizerOptions = Parameters<Fetch>[0]
 * - ResolveReturns = Awaited<ReturnType<Fetch>>
 *
 * @returns {ResolveReturns} The return type of the resolve
 */
export function mutualizerDecorator<
  Fetch extends (...args: any) => any = (...args: any) => any,
  MutualizerOptions = Parameters<Fetch>[0],
  ResolveReturns = Awaited<ReturnType<Fetch>>,
>({ fetch, merge, resolve, options }: {
  /** The asynchronous operation to be optimized (e.g., a function that makes an HTTP request). */
  fetch: TFetch<Parameters<Fetch>[0], Awaited<ReturnType<Fetch>>>
  /** The logic to merge the items in the bucket before making a batch request. */
  merge: TMerge<MutualizerOptions, Parameters<Fetch>[0]>
  /** The logic to resolve the promises for the completed requests. */
  resolve: TResolve<MutualizerOptions, Awaited<ReturnType<Fetch>>, ResolveReturns>
  /** optional configuration parameters, such as limit, throttle, sendBeforeUnload, and sanitizer. */
  options?: {
    /** sets the maximum number of items in the bucket before triggering a batch request */
    limit?: number
    /** sets the throttle time (in milliseconds) for batching requests */
    throttle?: number
    /** if enabled, ensure pending requests are processed before the page unloads. */
    sendBeforeUnload?: boolean
    /** remove duplicated request, eg: mark as read, we don't need to mark the same community twice */
    sanitizer?: (
      bucket: Item<MutualizerOptions, ResolveReturns>[],
      item: Item<MutualizerOptions, ResolveReturns>
    ) => Item<MutualizerOptions, ResolveReturns>[]
  }
}): (option: MutualizerOptions) => Promise<ResolveReturns> {
  let bucket: Item<MutualizerOptions, ResolveReturns>[] = []
  let timeout: NodeJS.Timeout | undefined

  const processBatchRequests = async () => {
    logger.debug(`[decorator mutualizer] process batch request for ${bucket.length} request`, bucket)

    timeout && clearTimeout(timeout)
    timeout = undefined

    if (!bucket.length)
      return // prevent doing a request on before unload if there is no request to do

    const items = bucket.slice()
    bucket = []

    const results = await fetch(merge(items.map(el => el[0])))

    items.forEach(bucketArg => resolve([results, bucketArg]))
  }

  if (options?.sendBeforeUnload)
    window.addEventListener('beforeunload', processBatchRequests)

  return (args) => {
    return new Promise<ResolveReturns>((resolve, reject) => {
      if (options?.sanitizer) {
        logger.debug('[decorator mutualizer] sanitize bucket', bucket)
        bucket = options?.sanitizer(bucket, [args, resolve, reject])
      }

      bucket.push([args, resolve, reject])

      if (bucket.length === (options?.limit ?? BUCKET_LIMIT))
        processBatchRequests()

      else if (!timeout)
        timeout = setTimeout(processBatchRequests, options?.throttle ?? THROTTLE_MS)
    })
  }
}
